import React from 'react'
import { useTranslation } from 'react-i18next'

import { MealOfDay, MealPlan, Nutrient, UserNutritionalNeeds } from '@/api/types'
import {
  MACROS,
  MICRO_NUTRIENTS,
  NUTRIENT_MAPPING,
  NUTRIENT_UNITS,
  SORTED_MEALS,
} from '../constants'
import { getNutrientsForUser, getNutrientsPerHouseholdMember } from '../utils'
import './DailyEnergyRequirements.css'

const NUTRIENTS: Nutrient[] = ['KCAL', ...MACROS, ...MICRO_NUTRIENTS]

function DailyRequirementRow({
  macro,
  current,
  target,
}: {
  macro: Nutrient
  current: number
  target: number
}) {
  const { t } = useTranslation()
  const isMicro = MICRO_NUTRIENTS.includes(macro)
  const currentVal = current || 0

  return (
    <>
      <div>
        {t(`nutrient.${macro}` as any)}
        <span className="measure_small">{macro === 'KCAL' ? ' (kcal)' : ' (g)'}</span>
      </div>
      <div>{currentVal > 1 ? Math.round(currentVal || 0) : currentVal.toFixed(3)}</div>
      <div>{isMicro ? '-' : Math.round(target || 0)}</div>
      <div>{isMicro ? '-' : Math.round(currentVal - target)}</div>
    </>
  )
}

type Props = {
  mealPlans: MealPlan[]
  nutritionalNeeds?: UserNutritionalNeeds
  mainUserId: number
}

export default function DailyEnergyRequirements({
  mealPlans,
  nutritionalNeeds,
  mainUserId,
}: Props) {
  const { t } = useTranslation()

  const [dailyNutrientsForUser, nutrientsPerHousehold] = React.useMemo(() => {
    const allScaledRecipes = mealPlans.flatMap((mp) => mp.scaledRecipes)
    return [
      getNutrientsForUser(allScaledRecipes, mainUserId),
      getNutrientsPerHouseholdMember(allScaledRecipes),
    ]
  }, [mealPlans, mainUserId])

  function getEnergy(mealOfDay: MealOfDay, userId: number) {
    return mealPlans
      .filter((mp) => mp.mealOfDay === mealOfDay)
      .reduce((acc, mp) => acc + getNutrientsForUser(mp.scaledRecipes, userId).energy, 0)
  }

  function getDailyNutrient(macro: Nutrient) {
    let value = dailyNutrientsForUser[NUTRIENT_MAPPING[macro]]

    if (NUTRIENT_UNITS[macro] === 'mg') {
      value = value / 1000
    }

    return value
  }

  function getEnergyTargetForMeal(mealOfDay: MealOfDay) {
    const energy = nutritionalNeeds ? nutritionalNeeds.mealsKcalNeed[mealOfDay] : 0

    return isNaN(energy) ? 0 : Math.round(energy)
  }

  function getHouseholdMemberEnergyFraction(userName: string) {
    const energy = nutrientsPerHousehold[userName].energy
    const totalEnergy = Object.values(nutrientsPerHousehold).reduce(
      (acc, curr) => acc + curr.energy,
      0,
    )

    return (energy / totalEnergy) * 100
  }

  return (
    <div className="DailyEnergyRequirements">
      <div className="dailyRequirements">
        <div className="title">{t('nutritionalNeeds.dailyRequirements')}</div>

        <div className="dailyRequirements_grid">
          <div className="headerCol">-</div>
          <div className="headerCol">Current</div>
          <div className="headerCol">Target</div>
          <div className="headerCol">±</div>
          {NUTRIENTS.map((macro, i) => (
            <DailyRequirementRow
              key={macro}
              macro={macro}
              current={getDailyNutrient(macro)}
              target={nutritionalNeeds ? nutritionalNeeds.nutrientNeeds[macro] : 0}
            />
          ))}
        </div>
      </div>
      <div className="requirementGroup">
        <div className="title">{t('nutritionalNeeds.mealDistribution')}</div>
        <div className="mealOfDay_energy_header mealOfDay_energy">
          <div></div>
          <div>Current</div>
          <div>Target</div>
        </div>
        {SORTED_MEALS.map((meal) => (
          <div key={meal} className="mealOfDay_energy">
            <div>{t(`mealOfDay.${meal}` as any)}</div>
            <div>{Math.round(getEnergy(meal, mainUserId))}</div>
            <div>{getEnergyTargetForMeal(meal)}</div>
          </div>
        ))}
      </div>
      <div className="requirementGroup">
        <div className="title">{t('nutritionalNeeds.household')}</div>
        <div className="mealOfDay_energy_header mealOfDay_energy">
          <div></div>
          <div>kcal</div>
          <div>%</div>
        </div>
        {Object.keys(nutrientsPerHousehold).map((userName) => (
          <div key={userName} className="mealOfDay_energy">
            <div>{userName}</div>
            <div>{Math.round(nutrientsPerHousehold[userName].energy)}</div>
            <div>{Math.round(getHouseholdMemberEnergyFraction(userName))}</div>
          </div>
        ))}
      </div>
    </div>
  )
}
